<template>
  <div>
    <b-card class="rounded-lg">
      <b-card-header>
        <h4>{{ isEdit ? $t('accounting_accounts_edit') : $t('accounting_accounts_create') }}</h4>
      </b-card-header>
      <b-card-body>
        <validation-observer ref="form">
          <b-form @submit.prevent="onSubmit">
            <b-row>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Code"
                  rules="max:50"
                >
                  <b-form-group
                    :label="$t('accounting_accounts_code')"
                    label-for="code"
                  >
                    <b-form-input
                      id="code"
                      v-model="form.code"
                      :placeholder="$t('accounting_accounts_code')"
                      :state="errors.length > 0 ? false : null"
                      maxlength="50"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                    <small class="text-muted d-block">{{ $t('accounting_accounts_code_optional') }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Name"
                  rules="required|max:255"
                >
                  <b-form-group
                    :label="$t('accounting_accounts_name')"
                    label-for="name"
                  >
                    <b-form-input
                      id="name"
                      v-model="form.name"
                      :placeholder="$t('accounting_accounts_name')"
                      :state="errors.length > 0 ? false : null"
                      maxlength="255"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Type"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_accounts_type')"
                    label-for="type"
                  >
                    <v-select
                      id="type"
                      v-model="form.type"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      :options="typeOptions"
                      :reduce="val => val.value"
                      :placeholder="$t('accounting_accounts_type')"
                      :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <!-- <b-col cols="12" md="6">
                <b-form-group
                  :label="$t('accounting_accounts_currency')"
                  label-for="currency"
                >
                  <b-form-input
                    id="currency"
                    v-model="form.currency"
                    :placeholder="$t('accounting_accounts_currency')"
                    maxlength="3"
                    style="text-transform: uppercase;"
                  />
                  <small class="text-muted">{{ $t('accounting_accounts_currency_help') }}</small>
                </b-form-group>
              </b-col> -->
              <b-col
                cols="12"
                md="6"
              >
                <validation-provider
                  #default="{ errors }"
                  name="Status"
                  rules="required"
                >
                  <b-form-group
                    :label="$t('accounting_accounts_status')"
                    label-for="status"
                  >
                    <v-select
                      id="status"
                      v-model="form.status"
                      :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
                      :options="statusOptions"
                      :reduce="val => val.value"
                      :placeholder="$t('accounting_accounts_status')"
                      :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
                      :state="errors.length > 0 ? false : null"
                    />
                    <small class="text-danger">{{ errors[0] }}</small>
                  </b-form-group>
                </validation-provider>
              </b-col>
              <b-col cols="12">
                <b-form-group
                  :label="$t('accounting_accounts_description')"
                  label-for="description"
                >
                  <b-form-textarea
                    id="description"
                    v-model="form.description"
                    rows="3"
                    :placeholder="$t('accounting_accounts_description')"
                  />
                </b-form-group>
              </b-col>
            </b-row>
            <b-row>
              <b-col
                cols="12"
                class="mt-2"
              >
                <b-button
                  type="submit"
                  variant="primary"
                  class="mr-1"
                  :disabled="submitting"
                >
                  {{ submitting ? $t('accounting_accounts_saving') : $t('accounting_accounts_save') }}
                </b-button>
                <b-button
                  variant="secondary"
                  @click="$router.go(-1)"
                >
                  {{ $t('accounting_accounts_cancel') }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </validation-observer>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardBody,
  BForm,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BButton,
  BRow,
  BCol,
} from 'bootstrap-vue'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import vSelect from 'vue-select'
import { mapGetters } from 'vuex'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default {
  components: {
    BCard,
    BCardHeader,
    BCardBody,
    BForm,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BButton,
    BRow,
    BCol,
    ValidationProvider,
    ValidationObserver,
    vSelect,
    ToastificationContent,
  },
  computed: {
    ...mapGetters({
      item: 'accounting/accounts/item',
      loadItem: 'accounting/accounts/loadItem',
    }),
    isEdit() {
      return !!this.$route.params.id
    },
  },
  data() {
    return {
      submitting: false,
      form: {
        code: '',
        name: '',
        type: null,
        currency: 'EGP',
        status: 'active',
        description: '',
      },
      typeOptions: [
        { value: 'bank', label: 'Bank', label_ar: 'بنك' },
        { value: 'cash', label: 'Cash', label_ar: 'نقدي' },
        { value: 'petty_cash', label: 'Petty Cash', label_ar: 'صندوق صغير' },
      ],
      statusOptions: [
        { value: 'active', label: 'Active', label_ar: 'نشط' },
        { value: 'inactive', label: 'Inactive', label_ar: 'غير نشط' },
        { value: 'closed', label: 'Closed', label_ar: 'مغلق' },
      ],
    }
  },
  async mounted() {
    await this.loadAccount()
  },
  methods: {
    prepareFormData() {
      const data = {
        name: this.form.name.trim(),
        type: this.form.type,
      }

      // Add optional fields only if they have values
      if (this.form.code && this.form.code.trim()) {
        data.code = this.form.code.trim()
      }

      // Currency: send if provided and valid (3 characters)
      if (this.form.currency && this.form.currency.trim()) {
        const currencyValue = this.form.currency.trim().toUpperCase()
        if (currencyValue.length === 3) {
          data.currency = currencyValue
        }
      }

      // Status: always include (defaults to active in API, but be explicit)
      if (this.form.status) {
        data.status = this.form.status
      }

      // Description: only if provided
      if (this.form.description && this.form.description.trim()) {
        data.description = this.form.description.trim()
      }

      return data
    },
    async onSubmit() {
      const isValid = await this.$refs.form.validate()
      if (!isValid) return

      this.submitting = true
      try {
        const formData = this.prepareFormData()

        if (this.isEdit) {
          await this.$store.dispatch('accounting/accounts/update', {
            id: this.$route.params.id,
            data: formData,
          })
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('accounting_accounts_messages_updated_success'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        } else {
          await this.$store.dispatch('accounting/accounts/create', formData)
          this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('accounting_accounts_messages_created_success'),
              icon: 'CheckIcon',
              variant: 'success',
            },
          })
        }
        this.$router.push({ name: 'accounting-accounts-list' })
      } catch (error) {
        let errorMessage = this.$t('accounting_accounts_messages_error')
        if (error.response && error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message
        } else if (error.response && error.response.data && error.response.data.errors) {
          const { errors } = error.response.data
          const firstError = Object.values(errors)[0]
          if (Array.isArray(firstError) && firstError.length > 0) {
            errorMessage = firstError[0]
          }
        }
        this.$toast({
          component: ToastificationContent,
          props: {
            title: errorMessage,
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      } finally {
        this.submitting = false
      }
    },
    async loadAccount() {
      if (this.isEdit) {
        await this.$store.dispatch('accounting/accounts/get', this.$route.params.id)
        if (this.item) {
          this.form = {
            code: this.item.code || '',
            name: this.item.name || '',
            type: (this.item.type && this.item.type.value) || null,
            currency: this.item.currency || 'EGP',
            status: (this.item.status && this.item.status.value) || 'active',
            description: this.item.description || '',
          }
        }
      }
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
